import * as yup from 'yup'

type YupCheckIbanProps = {
  formatErrMsg: string
  lengthErrMsg: string
}

export const yupCheckIban = (props: YupCheckIbanProps) =>
  yup
    .string()
    // .matches(/^([A-Z]{2})[A-Za-z0-9 ]+$/, props.formatErrMsg)
    // .min(15, props.minLengthErrMsg)
    .test(
      'ibanLength',
      props.lengthErrMsg,
      (iban: string | undefined) => iban == null || validateIbanLength(iban)
    )
    .test(
      'ibanCheckSum',
      props.formatErrMsg,
      (iban: string | undefined) => iban == null || validateIbanCheckSum(iban)
    )

export const yupCheckPlAccountNumber = (props: YupCheckIbanProps) =>
  yup
    .string()
    // .matches(/^([A-Z]{2})[A-Za-z0-9 ]+$/, props.formatErrMsg)
    // .min(15, props.minLengthErrMsg)
    .test(
      'ibanLength',
      props.lengthErrMsg,
      (accountNumber: string | undefined) =>
        accountNumber == null || validateIbanLength('PL' + accountNumber)
    )
    .test(
      'ibanCheckSum',
      props.formatErrMsg,
      (accountNumber: string | undefined) =>
        accountNumber == null || validateIbanCheckSum('PL' + accountNumber)
    )

/**
 * Checks that the total IBAN length is correct as per the country. If not, the IBAN is invalid
 * @param iban some string
 * @returns true if purified iban has length defined for country's IBAN
 */
export function validateIbanLength(iban: string): boolean {
  const ibanLength: { [key in string]?: number | null } = {
    AD: 24,
    AE: 23,
    AL: 28,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BI: 28,
    BR: 29,
    BY: 28,
    CH: 21,
    CR: 22,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    EG: 29,
    ES: 24,
    LC: 32,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GE: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IQ: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    LY: 25,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SC: 31,
    SD: 18,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    ST: 25,
    SV: 28,
    TL: 23,
    TN: 24,
    TR: 26,
    UA: 29,
    VA: 22,
    VG: 24,
    XK: 20,
  }
  const ibanMinLength = 15

  let _purifiedIban = iban.replace(/ /g, '')
  const _country = _purifiedIban.substring(0, 2).toUpperCase()
  const _length = ibanLength[_country]
  if (_length != null) {
    if (_purifiedIban.length !== _length) return false
  } else if (_purifiedIban.length < ibanMinLength) return false
  return true
}

/**
 * https://en.wikipedia.org/wiki/International_Bank_Account_Number
  An IBAN is validated by converting it into an integer and performing a basic mod-97 operation (as described in ISO 7064) on it. 
  If the IBAN is valid, the remainder equals 1.

  Move the four initial characters to the end of the string
  Replace each letter in the string with two digits, thereby expanding the string, where A = 10, B = 11, ..., Z = 35
  Interpret the string as a decimal integer and compute the remainder of that number on division by 97
  If the remainder is 1, the check digit test is passed and the IBAN might be valid.

  Example (fictitious United Kingdom bank, sort code 12-34-56, account number 98765432):

  • IBAN:		GB82 WEST 1234 5698 7654 32	
  • Rearrange:		W E S T12345698765432 G B82	
  • Convert to integer:		3214282912345698765432161182	
  • Compute remainder:		3214282912345698765432161182	mod 97 = 1
 * 
 * @param iban some string
 * @returns true if purified iban includes correct IBAN checksum
 */
export function validateIbanCheckSum(iban: string): boolean {
  function isDigit(char: string): boolean {
    return char >= '0' && char <= '9'
  }

  function translateChars(bban: string): string {
    let _translateChars = ''
    const _offset = 'A'.charCodeAt(0)
    for (let i = 0; i < bban.length; i++) {
      _translateChars =
        _translateChars +
        (isDigit(bban.charAt(i))
          ? bban.charAt(i)
          : (bban.charCodeAt(i) - _offset + 10).toString())
    }
    return _translateChars
  }

  let _purifiedIban = iban.replace(/ /g, '').toUpperCase()
  const _num = BigInt(
    translateChars(_purifiedIban.substring(4) + _purifiedIban.substring(0, 4))
  )
  const _mod = _num % BigInt(97)
  return _mod === BigInt(1)
}

export default yupCheckIban
